import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { errorToast, successToast } from "@telluscompany/design-system";

import { useLocalization } from "@i18n";
import { axiosDeleteData, axiosPostData } from "@shared/api/clients";

import {
  ADMIN_LICENSE_ASSIGNMENTS_ENDPOINT,
  ADMIN_LICENSE_ASSIGNMENTS_QUERY_KEY,
} from "./licenseAssignments.queries";
import { ADMIN_LICENSE_INVENTORY_QUERY_KEY } from "../../../license-inventory/api/license-inventory";
import {
  CreateAdminLicenseAssignmentRequest,
  CreateAdminLicenseAssignmentResponse,
} from "./licenseAssignments.types";

export const createLicenseAssignment = async (
  body: CreateAdminLicenseAssignmentRequest,
): Promise<CreateAdminLicenseAssignmentResponse> => {
  return axiosPostData<
    CreateAdminLicenseAssignmentResponse,
    CreateAdminLicenseAssignmentRequest
  >(ADMIN_LICENSE_ASSIGNMENTS_ENDPOINT, body);
};

export const deleteLicenseAssignment = async (id: string): Promise<void> => {
  await axiosDeleteData<void>(`${ADMIN_LICENSE_ASSIGNMENTS_ENDPOINT}/${id}`);
};

// `usedQuantity` / `availableQuantity` no inventário mudam quando uma alocação
// é criada/revogada; por isso invalidamos as duas chaves principais.
const invalidate = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({
    queryKey: ADMIN_LICENSE_ASSIGNMENTS_QUERY_KEY,
    exact: false,
  });
  queryClient.invalidateQueries({
    queryKey: ADMIN_LICENSE_INVENTORY_QUERY_KEY,
    exact: false,
  });
};

export const useCreateLicenseAssignmentMutation = () => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: createLicenseAssignment,
    onSuccess: () => {
      invalidate(queryClient);
      successToast({ description: translate("common.createSuccess") });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description: error.parsedMessage ?? translate("common.errorCreating"),
      });
    },
  });
};

export const useDeleteLicenseAssignmentMutation = () => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: deleteLicenseAssignment,
    onSuccess: () => {
      invalidate(queryClient);
      successToast({ description: translate("common.deleteSuccess") });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description: error.parsedMessage ?? translate("common.errorDeleting"),
      });
    },
  });
};
